import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeaderSection } from "../../../components/common/PageHeadSection";
import { Input, Skeleton, Space } from "antd";
import { HotelSelect } from "../../../components/common/HotelSelect";
import { BoardService } from "../../../services/BoardService";
import { useMutation, useQuery } from "react-query";
import { mdlTablePagination } from "../../../models/ui-models/TablePagination";
import { BoardTable } from "../../../components/board/BoardTable";
import { SFModal } from "../../../components/elements/SFModal";
import { BoardDetail } from "../../../components/board/BoardDetail";
import { toast } from "react-toastify";
import { mdlBoardListRequest } from "../../../models/service-models/board/BoardListRequest";
import { ObjectHelper } from "../../../utils/ObjectHelper";
import { mdlBoardDeleteRequest } from "../../../models/service-models/board/BoardDeleteRequest";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import { BoardCopy } from "../../../components/board/BoardCopy";
import { mdlBoard } from "../../../models/domain/Board";
import { HeaderButton } from "../../../models/ui-models/HeaderButton";
import { faFileExcel, faFilePdf, faPlus } from "@fortawesome/free-solid-svg-icons";
import { ExportModal } from "../../../components/common/ExportModal";
import { enmExportModalType } from "../../../models/enums/ExportModalType";
import _ from 'lodash';
import { enmExcelExportType } from "../../../utils/ExcelColumnMappings";


export const BoardDefinition = () => {
    const { t } = useTranslation();
    const [selectedHotel, setSelectedHotel] = useState<string>();
    const [request, setRequest] = useState<mdlBoardListRequest>({ pageNumber: 1, pageSize: 10 });
    const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
    const [openCopyModal, setOpenCopyModal] = useState<boolean>(false);
    const [selectedBoard, setSelectedBoard] = useState<string>();
    const [rowSelectionIds, setRowSelectionIds] = useState<string[]>([]);
    const [openModalExcelExport, setOpenModalExcelExport] = useState<boolean>(false);
    const [openModalPDFExport, setOpenModalPDFExport] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");

    const debouncedSearch = useCallback(
        _.debounce((value: string) => setSearch(value), 500),
        []
    );
    useEffect(() => {
        if (!data && selectedHotel)
            refetch();
    }, [selectedHotel]);


    const {
        data,
        isLoading,
        refetch
    } = useQuery(
        ["getBoards", selectedHotel, search, request],
        () => {
            if (selectedHotel)
                return BoardService.List({ ...request, hotelId: selectedHotel, name: search });
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!selectedHotel
        }
    );

    const handleHotelSelect = (hotelId: string) => {
        setSelectedHotel(hotelId);
    }
    const handleAddNew = () => {
        setSelectedBoard(undefined);
        setOpenDetailModal(true);
    }
    const handleTableParamsChange = (pagination: mdlTablePagination, filters: any) => {
        setRequest({ ...request, pageSize: pagination.pageSize ?? 10, pageNumber: pagination.current ?? 1 })
    }
    const handleEdit = (BoardId: string) => {
        setSelectedBoard(BoardId);
        setOpenDetailModal(true);
    }

    const handleDelete = (boardId: string) => {
        deleteBoard({ id: boardId, hotelId: selectedHotel! });
    }
    const { mutateAsync: deleteBoard, isLoading: mutateLoading, data: mutateData } = useMutation(
        (request: mdlBoardDeleteRequest) => BoardService.Delete(request),
        {
            onMutate(variables) {
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    toast.success(t("common.success"));
                    refetch();
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const handleDetailCallback = () => {
        setOpenDetailModal(false);
        refetch();
    }
    const handleCopyCallback = () => {
        setOpenCopyModal(false);
        refetch();
    }

    const getButtons = (): HeaderButton[] | undefined => {
        let _buttons: HeaderButton[] = [];
        _buttons.push({
            title: t("definitions.boards.addNew"),
            icon: faPlus,
            fnFunction: handleAddNew,
            className: "btn btn-white btn-dim btn-outline-primary"
        });
        _buttons.push({
            title: t("definitions.boards.copyToOtherHotels"),
            icon: faCopy,
            fnFunction: () => {
                if (rowSelectionIds.length > 0)
                    setOpenCopyModal(true)
                else
                    toast.error(t("definitions.boards.pleaseSelectBoard"))
            },
            className: "btn btn-white btn-dim btn-outline-primary"
        });
        _buttons.push({
            title: t("common.exportToExcel"),
            icon: faFileExcel,
            fnFunction: () => setOpenModalExcelExport(true),
            className: "btn btn-white btn-dim btn-outline-primary"
        });
        _buttons.push({
            title: t("common.pdf"),
            icon: faFilePdf,
            fnFunction: () => setOpenModalPDFExport(true),
            className: "btn btn-white btn-dim btn-outline-primary"
        });
        return _buttons;
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSearch(e.target.value);
    };

    return (
        <>
            <div className="nk-content ">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <PageHeaderSection
                                pageTitle={t("definitions.boards.title")}
                                buttons={selectedHotel ? getButtons() : undefined}

                            />
                            <div className="nk-block">
                                <div className="row g-gs">
                                    <div className="col-12">
                                        <div className="card card-bordered h-100">
                                            <div className="card-inner">
                                                {
                                                    <HotelSelect onChange={handleHotelSelect} />
                                                }
                                                {selectedHotel && (
                                                    <>
                                                        <hr />
                                                        <Space align="center" style={{ justifyContent: "flex-end", display: "flex" }}>
                                                            <Input.Search
                                                                placeholder={t("definitions.boards.name")}
                                                                onChange={handleSearchChange}
                                                            />
                                                        </Space>
                                                        <hr />
                                                    </>
                                                )}
                                                {
                                                    data
                                                    &&
                                                    <BoardTable
                                                        onDelete={handleDelete}
                                                        onEdit={handleEdit}
                                                        pagination={{
                                                            align: "center",
                                                            current: data?.body?.pageNumber,
                                                            pageSizeOptions: [10, 20, 50],
                                                            defaultPageSize: 10,
                                                            pageSize: request.pageSize,
                                                            defaultCurrent: 1,
                                                            responsive: true,
                                                            showSizeChanger: true,
                                                            total: data?.body?.resultCount,
                                                            showTotal: (total: number, range: [number, number]) => t("pagination.total") + ` ${data?.body?.totalCount} ` + t("pagination.result"),
                                                            locale: { items_per_page: "/ " + t("pagination.itemPerPage") }
                                                        }}
                                                        onTableParamsChange={handleTableParamsChange}
                                                        rowSelectionChange={(boards: mdlBoard[]) => setRowSelectionIds(boards?.map(b => b.id))}
                                                        dataSource={data?.body?.data} />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openDetailModal}
                toggler={() => { setOpenDetailModal(!openDetailModal) }}
                body={<BoardDetail id={selectedBoard} hotelId={selectedHotel} callback={handleDetailCallback} />}
                title={t("definitions.boards.detailTitle")}
            />
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openCopyModal}
                toggler={() => { setOpenCopyModal(!openCopyModal) }}
                body={<BoardCopy ids={rowSelectionIds} hotelId={selectedHotel} callback={handleCopyCallback} />}
                title={t("definitions.boards.copyTitle")}
            />
            <SFModal
                width={window.innerWidth * 30 / 100}
                open={openModalExcelExport}
                toggler={() => { setOpenModalExcelExport(!openModalExcelExport) }}
                body={<ExportModal excelExportType={enmExcelExportType.Board} name="boards" data={data?.body?.data} fnModalClose={() => setOpenModalExcelExport(!openModalExcelExport)} />}
                title={t("common.exportToExcel")}
            />
            <SFModal
                width={window.innerWidth * 30 / 100}
                open={openModalPDFExport}
                toggler={() => { setOpenModalPDFExport(!openModalPDFExport) }}
                body={<ExportModal excelExportType={enmExcelExportType.Board} name="boards" type={enmExportModalType.PDFTable} data={data?.body?.data} fnModalClose={() => setOpenModalPDFExport(!openModalPDFExport)} />}
                title={t("common.exportToPDF")}
            />
        </>

    );
};
